import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../../../context/AppContext';
import { isAuthenticated } from '../../../services/api';
import FieldFilter from '../../shared/FieldFilter';
import CollapsibleFilters from '../../shared/CollapsibleFilters';
import { TicketCardsSkeleton, TicketKanbanSkeleton } from '../../tickets/TicketListSkeleton';
import { getAppOriginFilterOptions } from '../../../services/appOriginService';
import {
  getStatusLabel,
  getStatusFilterOptions,
} from '../../../services/statusService';
import {
  getPriorityLabel,
  PRIORITY_FILTER_OPTIONS
} from '../../../services/priorityService';
import { DEMAND_TYPE_FILTER_OPTIONS } from '../../../services/demandTypeService';
import { Inbox, LayoutGrid, Search, SquareKanban } from 'lucide-react';
import styled, { keyframes } from 'styled-components';
import TicketKanbanView from './TicketKanbanView';
import TicketCard from '../../tickets/TicketCard';
import FilterPresetToolbar from './FilterPresetToolbar';
import { useCreateTicketModal } from '../../../context/CreateTicketModalContext';

const UNASSIGNED_RESPONSIBLE_FILTER = 'UNASSIGNED';

const TicketsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  box-sizing: border-box;
`;

const TicketsPanel = styled.div`
  border-radius: 16px;
  padding: 1.5rem;
  background: transparent;
  width: 100%;
  overflow: visible;
  box-sizing: border-box;
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
  gap: 0.5rem;

  h2 {
    margin: 0;
    font-size: 1rem;
    letter-spacing: -0.01em;
    color: var(--color-text-primary);
    font-weight: 600;
  }
`;

const PageSizeSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--color-light);
  padding: 0.3rem 0.6rem;
  border-radius: 8px;
  border: 1px solid var(--color-border-primary);

  label {
    font-size: 0.8rem;
    color: var(--color-text-secondary);
  }

  select {
    padding: 0.2rem 0.4rem;
    border: none;
    border-radius: 8px;
    background: var(--color-bg-secondary);
    color: var(--color-text-primary);
    font-size: 0.8rem;
    height: 24px;
    border: 1px solid var(--color-border-primary);
  }
`;

const TicketCount = styled.span`
  font-size: 0.8rem;
  color: var(--color-text-secondary);
  font-weight: 500;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const ViewToggle = styled.div`
  display: inline-flex;
  gap: 2px;
  padding: 2px;
  background: var(--color-bg-tertiary);
  border: 1px solid var(--color-border-primary);
  border-radius: 8px;
  overflow: hidden;
`;

const ViewToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.7rem;
  border: none;
  border-radius: 6px;
  background: ${({ $active }) => ($active ? 'var(--color-bg-primary)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)')};
  font-size: 0.8rem;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  cursor: pointer;
  box-shadow: ${({ $active }) => ($active ? 'var(--shadow-sm)' : 'none')};
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    color: var(--color-text-primary);
  }
`;

const FilterSection = styled.div`
  margin: 0.5rem 0;
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchAndFiltersForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
  width: 100%;
  box-sizing: border-box;
`;

const SearchInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
`;

const SearchButtonRow = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 12px;
  color: var(--color-text-muted);
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 0.75rem 0 2.25rem;
  border-radius: 8px;
  font-size: 0.875rem;
  box-sizing: border-box;
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-primary);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: var(--color-border-secondary);
  }

  &:focus {
    outline: none;
    border-color: var(--color-text-primary);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }

  &::placeholder {
    color: var(--color-text-muted);
  }
`;

const SearchButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  height: 40px;
  min-width: min(100%, 200px);
  padding: 0 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
  background: var(--color-btn-primary-bg);
  color: var(--color-btn-primary-text);
  border: 1px solid var(--color-btn-primary-border);
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.1s ease;

  &:hover:not(:disabled) {
    opacity: 0.88;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TicketsListScroller = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;

  ${({ $hideScrollbar }) =>
    $hideScrollbar &&
    `
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
  `}

  @media (max-width: 768px) {
    overflow-x: visible;
  }
`;

const refreshSlide = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(250%); }
`;

const ListArea = styled.div`
  position: relative;
  width: 100%;
`;

const RefreshBar = styled.div`
  position: absolute;
  top: 0.375rem;
  left: 0;
  right: 0;
  height: 2px;
  overflow: hidden;
  border-radius: 999px;
  background: var(--color-bg-tertiary);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    width: 40%;
    border-radius: inherit;
    background: var(--color-text-primary);
    animation: ${refreshSlide} 1.1s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation-duration: 2.4s;
    }
  }
`;

const TicketsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 0.75rem;
  margin-top: 1.25rem;
  align-items: stretch;
  width: 100%;
  opacity: ${({ $dimmed }) => ($dimmed ? 0.55 : 1)};
  pointer-events: ${({ $dimmed }) => ($dimmed ? 'none' : 'auto')};
  transition: opacity 0.2s ease;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

/** Ocupa todas as colunas (spinner, mensagem vazia) */
const TicketsListFullRow = styled.div`
  grid-column: 1 / -1;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 3rem 1rem;
  border: 1px dashed var(--color-border-secondary);
  border-radius: 12px;
  text-align: center;
  font-size: 0.875rem;
  color: var(--color-text-muted);

  svg {
    color: var(--color-border-accent);
  }
`;

const Pagination = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  margin-top: 2rem;
  padding: 1rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  background: var(--color-light);
  border-radius: 12px;
  border: 1px solid var(--color-border-primary);

  @media (max-width: 768px) {
    gap: 0.5rem;
    padding: 0.75rem;
    margin-top: 1.25rem;
  }
`;

const PaginationInfo = styled.span`
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  text-align: center;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const PaginationActions = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.5rem;
  width: 100%;
  max-width: 520px;

  button {
    padding: 0.5rem 0.65rem;
    border: 1px solid var(--color-border-primary);
    border-radius: 8px;
    background: var(--color-bg-primary);
    color: var(--color-text-primary);
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    font-size: 0.85rem;
    white-space: nowrap;
    min-width: 0;
    width: 100%;
    text-align: center;

    &:hover:not(:disabled) {
      border: 1px solid var(--color-border-primary);
      box-shadow: var(--shadow-sm);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      box-shadow: none;
    }
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    button {
      padding: 0.55rem 0.5rem;
      font-size: 0.82rem;
    }
  }
`;

/** Paginação minimalista (Meus Tickets): números + underline na ativa + setas */
const MinimalPagination = styled.nav`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1.25rem;
  margin-top: 1.75rem;
  padding: 0.5rem 0;
  width: 100%;
  background: transparent;
  border: none;
`;

const MinimalPageButton = styled.button`
  appearance: none;
  background: none;
  border: none;
  border-radius: 0;
  box-shadow: none;
  padding: 0.15rem 0.1rem 0.35rem;
  margin: 0;
  min-width: 1.25rem;
  font-size: 1rem;
  font-weight: 500;
  font-family: inherit;
  line-height: 1.2;
  color: ${({ $active }) =>
    $active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)'};
  border-bottom: 2px solid
    ${({ $active }) => ($active ? 'var(--color-text-primary)' : 'transparent')};
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) {
    color: var(--color-text-primary);
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const MinimalNavButton = styled.button`
  appearance: none;
  background: none;
  border: none;
  box-shadow: none;
  padding: 0.15rem 0.25rem;
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  line-height: 1;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover:not(:disabled) {
    color: var(--color-text-primary);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const getVisiblePageNumbers = (current, total) => {
  const safeTotal = Math.max(1, total || 1);
  const safeCurrent = Math.min(Math.max(1, current || 1), safeTotal);
  if (safeTotal <= 7) {
    return Array.from({ length: safeTotal }, (_, i) => i + 1);
  }
  let start = Math.max(1, safeCurrent - 2);
  let end = Math.min(safeTotal, start + 4);
  start = Math.max(1, end - 4);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

const SupportTicketList = ({
  mode = 'allTickets',
  listTitle = 'Tickets',
  lockDemandType = false,
  viewKey = 'all-tickets',
  demandTypeFilter = null,
  detailsBasePath = null,
  initialLoading = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isUserMode = mode === 'user';
  const { createdTicketsVersion } = useCreateTicketModal();
  const detailsPath =
    detailsBasePath || (isUserMode ? '/user/tickets' : '/all-tickets');

  const {
    tickets,
    loading,
    error,
    totalElements,
    selectedStatus,
    selectedPriority,
    selectedAppOrigin,
    pageSize,
    setPageSize: applyPageSizeChange,
    currentPage,
    totalPages,
    fetchTickets,
    handleStatusChange,
    handlePriorityChange,
    handleAppOriginChange,
    handlePageChange,
    selectedTicket,
    users,
    searchText,
    handleSearchTextChange,
    selectedDemandType,
    handleDemandTypeChange,
    selectedCompanyNames,
    handleCompanyNamesChange,
    companyNames,
    selectedResponsibleEmails,
    handleResponsibleEmailsChange,
    ticketStatuses,
    appOrigins,
    viewDemandTypePreset,
    userTickets,
    userLoading,
    userError,
    userTotalElements,
    userTotalPages,
    userCurrentPage,
    userSelectedStatus,
    userSelectedPriority,
    handleUserStatusChange,
    handleUserPriorityChange,
    fetchUserTickets,
    handleUserPageChange,
    handleUserPageSizeChange,
    listViewMode,
    setListViewMode,
    kanbanTickets,
    kanbanLoading,
    userListViewMode,
    setUserListViewMode,
    userKanbanTickets,
    userKanbanLoading,
    fetchUserKanbanTickets,
    filterPresets,
    activeFilterPresetId,
    applyFilterPresetById,
    saveCurrentFilterPreset,
    makeFilterPresetDefault,
    removeFilterPreset,
  } = useApp();

  const viewMode = isUserMode ? userListViewMode : listViewMode;
  const changeViewMode = isUserMode ? setUserListViewMode : setListViewMode;
  const isKanban = viewMode === 'kanban';
  const listTickets = isUserMode
    ? isKanban
      ? userKanbanTickets
      : userTickets
    : isKanban
      ? kanbanTickets
      : tickets;
  const listLoading = isUserMode
    ? isKanban
      ? userKanbanLoading
      : userLoading
    : isKanban
      ? kanbanLoading || loading
      : loading;
  const listError = isUserMode ? userError : error;
  const listTotalElements = isUserMode ? userTotalElements : totalElements;
  const listTotalPages = isUserMode ? userTotalPages : totalPages;
  const listCurrentPage = isUserMode ? userCurrentPage : currentPage;
  const listSelectedStatus = isUserMode ? userSelectedStatus : selectedStatus;

  const [localSelectedStatus, setLocalSelectedStatus] = useState(() =>
    Array.isArray(listSelectedStatus) ? [...listSelectedStatus] : []
  );

  const [localSelectedPriority, setLocalSelectedPriority] = useState(() =>
    isUserMode
      ? Array.isArray(userSelectedPriority)
        ? [...userSelectedPriority]
        : []
      : Array.isArray(selectedPriority)
        ? [...selectedPriority]
        : selectedPriority
          ? [selectedPriority]
          : []
  );
  const [localSelectedAppOrigin, setLocalSelectedAppOrigin] = useState(() =>
    Array.isArray(selectedAppOrigin) ? [...selectedAppOrigin] : selectedAppOrigin ? [selectedAppOrigin] : []
  );

  const [localSearchText, setLocalSearchText] = useState(searchText || '');
  const [localSelectedDemandType, setLocalSelectedDemandType] = useState(() =>
    Array.isArray(selectedDemandType) ? [...selectedDemandType] : []
  );
  const [localSelectedCompanyNames, setLocalSelectedCompanyNames] = useState(() =>
    Array.isArray(selectedCompanyNames) ? [...selectedCompanyNames] : []
  );
  const [localSelectedResponsibleEmails, setLocalSelectedResponsibleEmails] = useState(() =>
    Array.isArray(selectedResponsibleEmails) ? [...selectedResponsibleEmails] : []
  );

  const responsibleFilterOptions = useMemo(
    () =>
      [
        { value: UNASSIGNED_RESPONSIBLE_FILTER, label: 'Ninguém' },
        ...(users || [])
          .filter((u) => u?.email)
          .map((u) => ({
            value: u.email,
            label: u.name ? `${u.name} (${u.email})` : u.email,
          })),
      ],
    [users]
  );

  const listDemandTypeFilter = useMemo(() => {
    if (Array.isArray(viewDemandTypePreset) && viewDemandTypePreset.length === 1) {
      return viewDemandTypePreset[0];
    }
    return null;
  }, [viewDemandTypePreset]);

  const statusFilterOptions = useMemo(
    () => getStatusFilterOptions(listDemandTypeFilter),
    [ticketStatuses, listDemandTypeFilter]
  );

  const appOriginFilterOptions = useMemo(
    () => getAppOriginFilterOptions(),
    [appOrigins]
  );

  const companyFilterOptions = useMemo(
    () =>
      (companyNames || []).map((name) => ({
        value: name,
        label: name,
      })),
    [companyNames]
  );
  const [showInitialSpinner, setShowInitialSpinner] = useState(isUserMode);
  const initialLoadStartedRef = useRef(false);

  useEffect(() => {
    if (!isUserMode) return;
    setShowInitialSpinner(true);
    initialLoadStartedRef.current = false;
  }, [isUserMode]);

  const appliedStatusKey = useMemo(
    () =>
      JSON.stringify(
        [...(Array.isArray(listSelectedStatus) ? listSelectedStatus : [])].sort()
      ),
    [listSelectedStatus]
  );

  const appliedPriorityKey = useMemo(
    () => JSON.stringify([...(Array.isArray(selectedPriority) ? selectedPriority : [])].sort()),
    [selectedPriority]
  );
  const appliedUserPriorityKey = useMemo(
    () =>
      JSON.stringify([...(Array.isArray(userSelectedPriority) ? userSelectedPriority : [])].sort()),
    [userSelectedPriority]
  );
  const appliedAppOriginKey = useMemo(
    () => JSON.stringify([...(Array.isArray(selectedAppOrigin) ? selectedAppOrigin : [])].sort()),
    [selectedAppOrigin]
  );

  useEffect(() => {
    setLocalSelectedStatus(Array.isArray(listSelectedStatus) ? [...listSelectedStatus] : []);
    // Só reage quando o *conteúdo* aplicado no contexto muda (appliedStatusKey), não quando só muda a referência do array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedStatusKey]);

  useEffect(() => {
    if (isUserMode) {
      setLocalSelectedPriority(
        Array.isArray(userSelectedPriority) ? [...userSelectedPriority] : []
      );
    } else {
      setLocalSelectedPriority(
        Array.isArray(selectedPriority) ? [...selectedPriority] : selectedPriority ? [selectedPriority] : []
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserMode, appliedPriorityKey, appliedUserPriorityKey]);

  useEffect(() => {
    if (isUserMode) return;
    setLocalSelectedAppOrigin(
      Array.isArray(selectedAppOrigin) ? [...selectedAppOrigin] : selectedAppOrigin ? [selectedAppOrigin] : []
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedAppOriginKey, isUserMode]);

  useEffect(() => {
    if (!isUserMode) return;
    if (isKanban) {
      fetchUserKanbanTickets();
    } else {
      fetchUserTickets();
    }
  }, [isUserMode, isKanban, fetchUserTickets, fetchUserKanbanTickets, createdTicketsVersion]);

  useEffect(() => {
    if (!isUserMode || !showInitialSpinner) return;
    if (listLoading) initialLoadStartedRef.current = true;
    if (initialLoadStartedRef.current && !listLoading) setShowInitialSpinner(false);
  }, [listLoading, showInitialSpinner, isUserMode]);

  useEffect(() => {
    setLocalSearchText(searchText || '');
  }, [searchText]);

  useEffect(() => {
    if (lockDemandType) return;
    setLocalSelectedDemandType(Array.isArray(selectedDemandType) ? [...selectedDemandType] : []);
  }, [selectedDemandType, lockDemandType]);

  useEffect(() => {
    setLocalSelectedCompanyNames(
      Array.isArray(selectedCompanyNames) ? [...selectedCompanyNames] : []
    );
  }, [selectedCompanyNames]);

  useEffect(() => {
    setLocalSelectedResponsibleEmails(
      Array.isArray(selectedResponsibleEmails) ? [...selectedResponsibleEmails] : []
    );
  }, [selectedResponsibleEmails]);

  const handleSearchSubmit = (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    if (isUserMode) {
      handleUserStatusChange(localSelectedStatus);
      handleUserPriorityChange(localSelectedPriority);
      return;
    }
    handleSearchTextChange(localSearchText);
    if (!lockDemandType) {
      handleDemandTypeChange(localSelectedDemandType);
    }
    handleCompanyNamesChange(localSelectedCompanyNames);
    handleResponsibleEmailsChange(localSelectedResponsibleEmails);
    handleStatusChange(localSelectedStatus);
    handlePriorityChange(localSelectedPriority);
    handleAppOriginChange(localSelectedAppOrigin);
  };

  useEffect(() => {
    if (!isAuthenticated()) {
      console.log('User not authenticated, redirecting to login');
      navigate('/login');
    }
  }, [navigate]);

  const handlePageSizeChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    if (isUserMode) {
      handleUserPageSizeChange(newSize);
    } else {
      applyPageSizeChange(newSize);
    }
  };

  const onPageChange = isUserMode ? handleUserPageChange : handlePageChange;

  const activeFilterCount = useMemo(() => {
    const counts = [
      Array.isArray(localSelectedStatus) ? localSelectedStatus.length : 0,
      Array.isArray(localSelectedPriority) ? localSelectedPriority.length : 0,
    ];
    if (!isUserMode && !lockDemandType) {
      counts.push(Array.isArray(localSelectedDemandType) ? localSelectedDemandType.length : 0);
    }
    if (!isUserMode) {
      counts.push(Array.isArray(localSelectedAppOrigin) ? localSelectedAppOrigin.length : 0);
      counts.push(
        Array.isArray(localSelectedResponsibleEmails) ? localSelectedResponsibleEmails.length : 0
      );
      counts.push(Array.isArray(localSelectedCompanyNames) ? localSelectedCompanyNames.length : 0);
    }
    return counts.reduce((sum, n) => sum + (n > 0 ? 1 : 0), 0);
  }, [
    isUserMode,
    lockDemandType,
    localSelectedStatus,
    localSelectedPriority,
    localSelectedDemandType,
    localSelectedAppOrigin,
    localSelectedResponsibleEmails,
    localSelectedCompanyNames,
  ]);

  const showSkeleton =
    initialLoading || (isUserMode && showInitialSpinner) || (listLoading && !listTickets.length);
  const isRefreshing = listLoading && !showSkeleton;

  if (listError && !showSkeleton) {
    return <div className="error-message">{listError}</div>;
  }

  const noFiltersApplied = (
    (!selectedStatus || selectedStatus.length === 0) &&
    (!Array.isArray(selectedPriority) || selectedPriority.length === 0) &&
    (!Array.isArray(selectedAppOrigin) || selectedAppOrigin.length === 0)
  );

  const formattedSelectedStatuses = (Array.isArray(listSelectedStatus) ? listSelectedStatus : [])
    .map((code) => getStatusLabel(code))
    .join(', ');

  const appliedPriorityForEmptyMessage = isUserMode ? userSelectedPriority : selectedPriority;

  return (
    <TicketsContainer>
      <TicketsPanel $isKanban={isKanban}>
        <HeaderTop>
          <h2>{isUserMode ? 'Tickets' : listTitle}</h2>
          <HeaderActions>
            <ViewToggle role="group" aria-label="Modo de visualização">
              <ViewToggleBtn
                type="button"
                $active={!isKanban}
                aria-pressed={!isKanban}
                onClick={() => changeViewMode('cards')}
              >
                <LayoutGrid size={14} strokeWidth={2} aria-hidden />
                Cards
              </ViewToggleBtn>
              <ViewToggleBtn
                type="button"
                $active={isKanban}
                aria-pressed={isKanban}
                onClick={() => changeViewMode('kanban')}
              >
                <SquareKanban size={14} strokeWidth={2} aria-hidden />
                Kanban
              </ViewToggleBtn>
            </ViewToggle>
            {!isKanban && (
              <PageSizeSelector>
                <label htmlFor="page-size-select">Itens por página:</label>
                <select id="page-size-select" value={pageSize} onChange={handlePageSizeChange}>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </select>
              </PageSizeSelector>
            )}
            <TicketCount aria-live="polite">
              {showSkeleton
                ? 'Carregando…'
                : listTotalElements > 0
                  ? `Total: ${listTotalElements}`
                  : 'Nenhum ticket'}
            </TicketCount>
          </HeaderActions>
        </HeaderTop>

        {!isUserMode && (
          <FilterPresetToolbar
            presets={filterPresets}
            activePresetId={activeFilterPresetId}
            onSelectPreset={applyFilterPresetById}
            onSavePreset={saveCurrentFilterPreset}
            onSetDefault={makeFilterPresetDefault}
            onDeletePreset={removeFilterPreset}
            disabled={listLoading}
          />
        )}

        <SearchAndFiltersForm onSubmit={handleSearchSubmit}>
          {!isUserMode && (
            <SearchInputRow>
              <SearchInputWrapper>
                <SearchIcon />
                <SearchInput
                  type="text"
                  placeholder="Busca geral (protocolo, assunto, descrição, e-mail, empresa...)"
                  value={localSearchText}
                  onChange={(e) => setLocalSearchText(e.target.value)}
                />
              </SearchInputWrapper>
            </SearchInputRow>
          )}

          <CollapsibleFilters activeCount={activeFilterCount}>
            <FilterSection className="filter-section">
              <FieldFilter
                label="Status:"
                fieldId={isUserMode ? 'user-ticket-status-filter' : 'ticket-status-filter'}
                selectedValues={localSelectedStatus}
                onValuesChange={setLocalSelectedStatus}
                items={statusFilterOptions}
                disabled={listLoading}
              />
              <FieldFilter
                label="Prioridade:"
                fieldId={isUserMode ? 'user-ticket-priority-filter' : 'ticket-priority-filter'}
                selectedValues={localSelectedPriority}
                onValuesChange={setLocalSelectedPriority}
                items={PRIORITY_FILTER_OPTIONS}
                disabled={listLoading}
              />
              {!isUserMode && !lockDemandType && (
                <FieldFilter
                  label="Tipo de demanda:"
                  fieldId="ticket-demand-type-filter"
                  selectedValues={localSelectedDemandType}
                  onValuesChange={setLocalSelectedDemandType}
                  items={DEMAND_TYPE_FILTER_OPTIONS}
                  disabled={listLoading}
                />
              )}
              {!isUserMode && (
                <FieldFilter
                  label="Origem do App:"
                  fieldId="ticket-app-origin-filter"
                  selectedValues={localSelectedAppOrigin}
                  onValuesChange={setLocalSelectedAppOrigin}
                  items={appOriginFilterOptions}
                  disabled={listLoading}
                />
              )}
              {!isUserMode && responsibleFilterOptions.length > 0 && (
                <FieldFilter
                  label="Responsável:"
                  fieldId="ticket-responsible-filter"
                  selectedValues={localSelectedResponsibleEmails}
                  onValuesChange={setLocalSelectedResponsibleEmails}
                  items={responsibleFilterOptions}
                  disabled={listLoading}
                />
              )}
              {!isUserMode && companyFilterOptions.length > 0 && (
                <FieldFilter
                  label="Empresa:"
                  fieldId="ticket-company-filter"
                  selectedValues={localSelectedCompanyNames}
                  onValuesChange={setLocalSelectedCompanyNames}
                  items={companyFilterOptions}
                  disabled={listLoading}
                />
              )}
            </FilterSection>
          </CollapsibleFilters>

          <SearchButtonRow>
            <SearchButton type="submit">Buscar</SearchButton>
          </SearchButtonRow>
        </SearchAndFiltersForm>

        <ListArea aria-busy={showSkeleton || isRefreshing || undefined}>
        {isRefreshing && <RefreshBar role="progressbar" aria-label="Atualizando tickets" />}
        {showSkeleton ? (
          isKanban ? (
            <TicketKanbanSkeleton />
          ) : (
            <TicketCardsSkeleton count={Math.min(Number(pageSize) || 8, 12)} />
          )
        ) : isKanban ? (
          <TicketKanbanView
            tickets={listTickets}
            loading={listLoading}
            demandTypeFilter={demandTypeFilter}
            statusFilter={listSelectedStatus}
            detailsPath={detailsPath}
            listReturnPath={location.pathname}
            readOnly={isUserMode}
          />
        ) : (
        <TicketsListScroller $hideScrollbar={isUserMode}>
        <TicketsList $dimmed={isRefreshing}>
          {listTickets.map((ticket) =>
            ticket?.id ? (
              <TicketCard
                key={ticket.id}
                ticket={ticket}
                users={users}
                to={`${detailsPath}/${ticket.id}`}
                linkState={{ returnTo: location.pathname }}
                selected={selectedTicket?.id === ticket.id}
                showStatus
                onOpenComments={() =>
                  navigate(`${detailsPath}/${ticket.id}#ticket-messages`, {
                    state: { returnTo: location.pathname },
                  })
                }
              />
            ) : null
          )}
          {!listLoading && listTickets.length === 0 && (
            <TicketsListFullRow>
              <EmptyState>
                <Inbox size={24} strokeWidth={1.75} aria-hidden />
                <span>
                Nenhum ticket encontrado
                {formattedSelectedStatuses ? ` com status: ${formattedSelectedStatuses}` : ''}
                {(Array.isArray(appliedPriorityForEmptyMessage) && appliedPriorityForEmptyMessage.length > 0)
                  ? ` e prioridade: ${appliedPriorityForEmptyMessage.map(getPriorityLabel).join(', ')}`
                  : ''}
                {!isUserMode &&
                ((Array.isArray(selectedAppOrigin) && selectedAppOrigin.length > 0)
                  ? ` e origem: ${selectedAppOrigin.join(', ')}`
                  : '')}
                .
                </span>
              </EmptyState>
            </TicketsListFullRow>
          )}
        </TicketsList>
        </TicketsListScroller>
        )}
        </ListArea>

        {!isKanban && isUserMode && !showSkeleton && (
          <MinimalPagination aria-label="Paginação">
            <MinimalNavButton
              type="button"
              aria-label="Página anterior"
              disabled={listCurrentPage <= 1}
              onClick={() => onPageChange(listCurrentPage - 1)}
            >
              ‹
            </MinimalNavButton>
            {getVisiblePageNumbers(listCurrentPage, listTotalPages).map((pageNum) => (
              <MinimalPageButton
                key={pageNum}
                type="button"
                $active={pageNum === listCurrentPage}
                aria-current={pageNum === listCurrentPage ? 'page' : undefined}
                aria-label={`Página ${pageNum}`}
                onClick={() => onPageChange(pageNum)}
              >
                {pageNum}
              </MinimalPageButton>
            ))}
            <MinimalNavButton
              type="button"
              aria-label="Próxima página"
              disabled={listCurrentPage >= listTotalPages || listTotalPages <= 1}
              onClick={() => onPageChange(listCurrentPage + 1)}
            >
              ›
            </MinimalNavButton>
          </MinimalPagination>
        )}

        {!isKanban && !isUserMode && !showSkeleton && (
        <Pagination>
          <PaginationInfo>
            Página {listCurrentPage} de {listTotalPages > 0 ? listTotalPages : 1}
          </PaginationInfo>
          <PaginationActions>
            <button
              type="button"
              onClick={() => onPageChange(1)}
              disabled={listCurrentPage === 1}
            >
              Primeira
            </button>
            <button
              type="button"
              onClick={() => onPageChange(listCurrentPage - 1)}
              disabled={listCurrentPage <= 1}
            >
              Anterior
            </button>
            <button
              type="button"
              onClick={() => onPageChange(listCurrentPage + 1)}
              disabled={listCurrentPage >= listTotalPages}
            >
              Próxima
            </button>
            <button
              type="button"
              onClick={() => onPageChange(listTotalPages)}
              disabled={listCurrentPage === listTotalPages || listTotalPages <= 1}
            >
              Última
            </button>
          </PaginationActions>
        </Pagination>
        )}
      </TicketsPanel>
    </TicketsContainer>
  );
};

export default SupportTicketList;